import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ApiResponse, User } from '../models';
import { Router } from '@angular/router';
import { TokenResultDto } from '../models/tokenResultDto';
import { BaseService } from './Base.service';
import { PageAccessStateService } from './page-access-state.service';
import { TenantFeaturesService } from './tenant-features.service';
import { BillingService } from './billing.service';
import { TenantDto } from '../models/tenant';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private authApiUrl = `${environment.apiUrl}/Auth`;
  private tenentApiUrl = `${environment.apiUrl}/Tenants`;
  private headers: HttpHeaders;
  private userSubject: BehaviorSubject<User | null>;
  public user: Observable<User | null>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private pageAccessState: PageAccessStateService,
    private tenantFeatures: TenantFeaturesService,
    private billing: BillingService,
  ) {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-ID': this.getTenantId(),
    });

    this.userSubject = new BehaviorSubject(
      JSON.parse(localStorage.getItem('user')!)
    );
    this.user = this.userSubject.asObservable();
  }

  private getTenantId(): string {
    return (
      localStorage.getItem('tenantId') || 'c73214d5-f621-450f-b8f6-ce629128a3f3'
    );
  }

  public get userValue() {
    return this.userSubject.value;
  }

  getTenentIdByURL(): Observable<ApiResponse<TenantDto>> {
    const params = new URLSearchParams({ url: window.location.origin }).toString();
    return this.http.get<ApiResponse<TenantDto>>(
      `${this.tenentApiUrl}/GetByURL?${params}`
    );
  }

  login(
    userName: string,
    password: string
  ): Observable<ApiResponse<TokenResultDto>> {
    const loginData = { userName, password };

    return this.http
      .post<ApiResponse<TokenResultDto>>(
        `${this.authApiUrl}/login`,
        loginData
      )
      .pipe(
        tap((response) => {
          if (response.isSuccess && response.value?.token) {
            localStorage.setItem('authToken', response.value.token);
            // Store user object so userSubject stays in sync
            const user = { token: response.value.token };
            localStorage.setItem('user', JSON.stringify(user));
            this.userSubject.next(user as any);
          }
        })
      );
  }

  logout() {
    // remove user from local storage and set current user to null
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    this.userSubject.next(null);
    // Drop everything cached for this user so the next sign-in in the same tab starts clean.
    this.pageAccessState.clear();
    this.tenantFeatures.clear();
    this.billing.clear();
    this.router.navigate(['/account/login']);
  }

  resetPassword(payload: any): Observable<any> {
    return this.http.post(`${this.authApiUrl}/reset-password`, payload);
  }
}
